/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { decorate, observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import { displaySuccess } from '@amzn/base-ui/dist/helpers/notification';

import {
  buildScheduleValue,
  parseScheduleValue,
  defaultDays,
  MODE_NONE,
  MODE_GROUPS,
  MODE_CUSTOM,
} from '../../../helpers/schedule-tag-format';
import { summariseWindows, timeOptions, idleStopOptions } from '../../../helpers/schedule-display';
import WorkspaceScheduleModeToggle from './WorkspaceScheduleModeToggle';
import WorkspaceScheduleGroupList from './WorkspaceScheduleGroupList';
import WorkspaceScheduleDayGrid from './WorkspaceScheduleDayGrid';
import WorkspaceScheduleSummary from './WorkspaceScheduleSummary';
import WorkspaceIdleStopSection from './WorkspaceIdleStopSection';
import WorkspaceIdleActivityChart from './WorkspaceIdleActivityChart';

/**
 * The workspace scheduling modal: the shell, the state, and the two write actions.
 *
 * Structured after `ModifyStorageSizeModal.js` (Requirement 15.1): a plain component with MobX
 * observables initialised in the constructor inside `runInAction` and declared through `decorate` at
 * the bottom, exported as `observer(...)`, rendering null when closed and a portal when open, and
 * receiving its API functions as props rather than importing them. Taking them as props is what lets
 * this file be rendered in a test without a store or a network stub.
 *
 * This is the only file in the group that holds observable state (Requirement 16.10). The child
 * components are presentational and receive values and callbacks as props, which keeps the observable
 * surface in one place and makes each child renderable on its own. That includes the tabs and the
 * activity graph: the active tab and the fetched activity are observables here.
 *
 * The two features sit on two tabs, Schedule and Idle stop, each opening with a line of plain
 * subtext saying what it does, in the style of the create workspace screen. That subtext replaced a
 * collapsed "How this works" panel, which users did not open. Save covers both tabs, and a tab with
 * unsaved changes carries a marker so an edit on the other tab is not forgotten.
 *
 * The Idle stop tab shows the workspace's hourly CPU since creation with the chosen threshold drawn
 * across it, and an estimate of how often the chosen settings would have stopped it in the last 14
 * days. The activity is read once per open, when the tab is first shown, so the Schedule tab costs no
 * CloudWatch call.
 *
 * The values presented for editing are read live from the instance on every open (Requirement 11.6),
 * never from the Tag_Mirror on the environment record. The mirror lags by up to a poll cycle and can
 * be wrong in a way the user cannot see, so it is used here for exactly one thing: comparing against
 * what was just read, so that settings changed outside Service Workbench can be pointed out
 * (Requirement 11.7).
 *
 * There is deliberately no line under the header saying what is currently set. It was removed by
 * request: the mode toggle, the preset picker, the day grid, the window summary and the idle control
 * below all read from the same saved state on open, so the line restated in prose what five controls
 * were already showing. `describeSavedState` went with it.
 *
 * The icons here carry no inline style. Alignment is done in `modals.css` per block, the same way
 * `.workspace-action-btn i.icon` and `.form-section-header i.icon` do it, so that there is one
 * mechanism rather than a style object repeated at every call site.
 *
 * The schedule and the idle timeout are independent features, written to two independent tags, and
 * they stay independent here: `canSubmitSchedule` and `canSubmitIdleStop` decide separately whether
 * each section has anything to write. There is one Save, because two settings in one dialog is not two
 * dialogs, but it sends only the sections that changed and attempts each one whatever the other did.
 *
 * A save that fully succeeds closes the modal and says so through the shared toast, like
 * `ModifyStorageSizeModal`. Only failures are rendered in here, and only they hold the modal open. See
 * `handleSave` for why the two directions are reported in different places.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - environment: the ScEnvironment (needs `.id`, and the mirror fields for the drift comparison)
 * - getSchedulingInfo: (envId) => Promise<SchedulingInfo>
 * - onSaveSchedule: (scheduleValue) => Promise, '' removes the schedule
 * - onSaveIdleStop: (idleStopMinutes, cpuThreshold) => Promise, null minutes removes the timeout
 * - getIdleActivity: (envId) => Promise<IdleActivity>, optional; without it the graph is not shown
 */

const TAB_SCHEDULE = 'schedule';
const TAB_IDLE = 'idle';
// Idle Stop comes first and is the tab the modal opens on: it is the setting we most want users to
// turn on, since it stops a forgotten workspace without them having to plan a timetable.
const TABS = [
  { key: TAB_IDLE, label: 'Idle Stop', icon: 'hourglass half' },
  { key: TAB_SCHEDULE, label: 'Schedule', icon: 'calendar alternate outline' },
];
const DEFAULT_TAB = TAB_IDLE;

const MODAL_TITLE = 'Auto Start and Stop';

// Times are 24-hour, as the time pickers and the Schedule Summary show them.
const SCHEDULE_EXAMPLE =
  'Example: with a schedule of 08:00 to 18:00, if you stop your workspace at 14:00, it stays off until 08:00 ' +
  'the next day.';

const IDLE_SUBTITLE =
  "Stops your workspace automatically when it's not in use, to avoid unnecessary costs. It counts as idle " +
  'once CPU stays below the threshold for the time you choose. Reading or typing uses very little CPU, so ' +
  'save your work before stepping away.';

/**
 * Normalises a Schedule_Tag value for comparison. Absent, null and empty all mean no timetable.
 */
function normaliseScheduleValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Normalises an idle timeout for comparison. Anything unusable means no timeout.
 */
function normaliseMinutes(value) {
  if (_.isNil(value) || value === '') return null;

  const minutes = Number(value);
  return Number.isFinite(minutes) ? minutes : null;
}

/**
 * True when a value the write endpoints return carries the blocks this modal reads.
 *
 * Both write endpoints return a fresh SchedulingInfo, so a successful save normally needs no second
 * read. A caller that resolves with something else is not treated as an error: the modal re-reads
 * instead, which keeps the file working against a store action that returns nothing.
 */
function isSchedulingInfo(value) {
  return _.isPlainObject(value) && (_.isPlainObject(value.schedule) || _.isPlainObject(value.idleStop));
}

/**
 * The reason a write failed, as the backend gave it, or a fallback when it gave none.
 *
 * The backend's validation errors name the offending value and the rule it broke, so they are worth
 * surfacing verbatim rather than replacing with a generic apology.
 */
function reasonFor(error) {
  const message = _.get(error, 'message');

  return typeof message === 'string' && message.trim().length > 0 ? message.trim() : 'please try again.';
}

class WorkspaceSchedulingModal extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.loading = false;
      this.submitting = false;
      this.error = '';
      this.info = null;
      this.mode = MODE_NONE;
      this.selectedGroups = [];
      this.days = defaultDays();
      this.idleStopMinutes = null;
      this.driftNotice = false;
      this.cpuThreshold = null;
      this.activeTab = DEFAULT_TAB;
      this.activity = null;
      this.activityLoading = false;
      this.activityError = '';
      this.activityRequested = false;
    });
  }

  componentDidMount() {
    if (this.props.open) {
      this.fetchSchedulingInfo();
      this.fetchActivityForDefaultTab();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      runInAction(() => {
        this.activeTab = DEFAULT_TAB;
        this.activity = null;
        this.activityError = '';
        this.activityRequested = false;
      });
      this.fetchSchedulingInfo();
      this.fetchActivityForDefaultTab();
    }
  }

  // The activity graph is only read when its tab is shown, and that is now the tab the modal opens on.
  fetchActivityForDefaultTab() {
    if (DEFAULT_TAB === TAB_IDLE) this.fetchIdleActivity();
  }

  /**
   * Reads the activity graph's data, once per open. A failure is shown in the graph's place and does
   * not affect the settings, which do not depend on it.
   */
  fetchIdleActivity = async () => {
    const { environment, getIdleActivity } = this.props;
    if (!getIdleActivity || this.activityRequested) return;

    runInAction(() => {
      this.activityRequested = true;
      this.activityLoading = true;
      this.activityError = '';
    });

    try {
      const activity = await getIdleActivity(environment.id);
      runInAction(() => {
        this.activity = _.isPlainObject(activity) ? activity : null;
        this.activityLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.activityError = reasonFor(err);
        this.activityLoading = false;
      });
    }
  };

  /**
   * Reads the authoritative state from the instance.
   *
   * `resets` is passed straight through to `applyInfo`, so that a read taken after a partial save can
   * refresh the section that was written while leaving the section that failed showing what the user
   * entered. On the initial open both default to true, which is the full reset.
   */
  fetchSchedulingInfo = async resets => {
    const { environment, getSchedulingInfo } = this.props;
    runInAction(() => {
      this.loading = true;
      this.error = '';
      this.info = null;
      this.driftNotice = false;
    });

    try {
      const info = await getSchedulingInfo(environment.id);
      runInAction(() => {
        this.applyInfo(info, resets);
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Failed to load the scheduling settings. Please try again.';
        this.loading = false;
      });
    }
  };

  /**
   * Loads a SchedulingInfo response into the controls.
   *
   * The schedule value is turned back into `mode`, `selectedGroups` and `days` by
   * `parseScheduleValue`, so the controls open showing what is actually set rather than a default the
   * user would have to recognise as not theirs. The parsing lives in the grammar helper, not here
   * (Requirement 16.3).
   *
   * The two sections are reset independently, so that saving one does not discard an unsaved edit to
   * the other.
   */
  applyInfo(info, { resetScheduleControls = true, resetIdleControls = true } = {}) {
    this.info = _.isPlainObject(info) ? info : null;

    if (resetScheduleControls) {
      const parsed = parseScheduleValue(normaliseScheduleValue(_.get(info, 'schedule.value')), this.groupNames);
      this.mode = parsed.mode;
      this.selectedGroups = parsed.selectedGroups;
      this.days = parsed.days;
    }

    if (resetIdleControls) {
      this.idleStopMinutes = normaliseMinutes(_.get(info, 'idleStop.minutes'));
      // What is set, else what this instance type calls for, else what the scheduler applies anyway.
      const candidates = [
        'idleStop.cpuThreshold',
        'idleStop.recommendedCpuThreshold',
        'idleStop.effectiveCpuThreshold',
      ];
      this.cpuThreshold = _.find(
        candidates.map(path => normaliseMinutes(_.get(info, path))),
        value => value !== null,
      );
      if (this.cpuThreshold === undefined) this.cpuThreshold = null;
    }

    this.driftNotice = this.computeDrift(info);
  }

  /**
   * True when what was just read from the instance disagrees with the Tag_Mirror (Requirement 11.7).
   *
   * The tags are the single source of truth, so the tag values are what the modal presents; the
   * mirror is only consulted to decide whether to say that something changed outside Service
   * Workbench. Case is ignored, because a value written by hand in different case is the same
   * timetable and reporting it as a change would train the user to ignore the notice.
   */
  computeDrift(info) {
    const environment = this.props.environment;
    if (!_.isPlainObject(info) || !environment) return false;

    const liveSchedule = normaliseScheduleValue(_.get(info, 'schedule.value')).toUpperCase();
    const mirrorSchedule = normaliseScheduleValue(environment.schedule).toUpperCase();
    if (liveSchedule !== mirrorSchedule) return true;

    return normaliseMinutes(_.get(info, 'idleStop.minutes')) !== normaliseMinutes(environment.idleStopMinutes);
  }

  get groups() {
    const groups = _.get(this.info, 'schedule.groups');
    return _.isArray(groups) ? groups : [];
  }

  get groupNames() {
    return this.groups.map(group => _.get(group, 'name')).filter(name => typeof name === 'string');
  }

  get groupsAvailable() {
    // Only an explicit false means the groups table could not be read, so an older response without
    // the field does not present a working account as broken.
    return _.get(this.info, 'schedule.groupsAvailable') !== false;
  }

  get timezone() {
    return _.get(this.info, 'timezone');
  }

  get intervalMinutes() {
    return normaliseMinutes(_.get(this.info, 'intervalMinutes'));
  }

  /** The times offered, at multiples of the scheduler's invocation interval (Requirement 5.4). */
  get timeOptions() {
    return timeOptions(this.intervalMinutes);
  }

  /** The idle timeouts offered, never below the deployment floor (Requirement 7.2). */
  get idleOptions() {
    return idleStopOptions(normaliseMinutes(_.get(this.info, 'idleMinMinutes')));
  }

  get scheduleModel() {
    return { mode: this.mode, selectedGroups: this.selectedGroups, days: this.days };
  }

  /** The value the controls currently describe. Built by the grammar helper, never assembled here. */
  get scheduleValue() {
    return buildScheduleValue(this.scheduleModel);
  }

  get savedScheduleValue() {
    return normaliseScheduleValue(_.get(this.info, 'schedule.value'));
  }

  get savedIdleStopMinutes() {
    return normaliseMinutes(_.get(this.info, 'idleStop.minutes'));
  }

  /** The CPU threshold tag as set, or null when the scheduler is applying its default. */
  get savedCpuThreshold() {
    return normaliseMinutes(_.get(this.info, 'idleStop.cpuThreshold'));
  }

  get recommendedCpuThreshold() {
    return normaliseMinutes(_.get(this.info, 'idleStop.recommendedCpuThreshold'));
  }

  get cpuThresholdOptions() {
    const options = _.get(this.info, 'idleStop.cpuThresholdOptions');
    return _.isArray(options) ? options.filter(value => Number.isFinite(value)) : [];
  }

  /** The GPU threshold the scheduler applies, for the graph. */
  get gpuThreshold() {
    return normaliseMinutes(_.get(this.info, 'idleStop.effectiveGpuThreshold'));
  }

  /**
   * True while a timeout is set but no threshold tag is, so the scheduler is applying its default
   * rather than the threshold the modal opened on. Said so, rather than leaving the control to imply
   * the recommended value is already in force.
   */
  get thresholdNotYetApplied() {
    return this.savedIdleStopMinutes !== null && this.savedCpuThreshold === null && this.cpuThreshold !== null;
  }

  get canUserSetIdleStop() {
    return _.get(this.info, 'idleStop.canUserSet') !== false;
  }

  get busy() {
    return this.loading || this.submitting;
  }

  /**
   * Whether the schedule section has anything to write, decided independently of the idle timeout.
   *
   * An empty value is included, and it is what removes the schedule. This once returned false for an
   * empty value on the reasoning that removal was its own action (Requirement 5.8) and that a confirm
   * button should not silently remove a schedule. Neither half of that holds now: mode Off is an
   * explicit, visible choice that builds the empty value, and the summary below reflects it the moment
   * it is chosen, so saving it removes nothing silently. A separate Remove button for a state the mode
   * toggle already expresses was a fourth footer button for a choice the user had already made.
   */
  get canSubmitSchedule() {
    if (this.busy || !this.info) return false;

    return this.scheduleValue !== this.savedScheduleValue;
  }

  /** Whether the idle section has anything to write, decided independently of the schedule. */
  get canSubmitIdleStop() {
    if (this.busy || !this.info) return false;
    if (!this.canUserSetIdleStop) return false;

    if (this.idleStopMinutes !== this.savedIdleStopMinutes) return true;
    // The threshold only matters with a timeout; turning the timeout off removes it anyway.
    return this.idleStopMinutes !== null && this.cpuThreshold !== this.savedCpuThreshold;
  }

  /**
   * Whether Save does anything.
   *
   * The two section getters stay separate because they decide which writes are sent; this one only
   * decides whether the button is live, which is true when either has something to send.
   */
  get canSave() {
    return this.canSubmitSchedule || this.canSubmitIdleStop;
  }

  get summaryLines() {
    return summariseWindows(this.scheduleModel, this.groups);
  }

  handleModeChange = mode => {
    runInAction(() => {
      this.mode = mode;
    });
  };

  handleGroupsChange = selectedGroups => {
    runInAction(() => {
      this.selectedGroups = selectedGroups;
    });
  };

  handleDayChange = (day, nextValue) => {
    runInAction(() => {
      // Reassigned rather than mutated in place, so that the map handed to the grid is a plain object
      // the children can read without knowing anything about MobX.
      this.days = { ...this.days, [day]: nextValue };
    });
  };

  handleIdleStopChange = minutes => {
    runInAction(() => {
      this.idleStopMinutes = normaliseMinutes(minutes);
    });
  };

  handleCpuThresholdChange = percent => {
    runInAction(() => {
      this.cpuThreshold = normaliseMinutes(percent);
    });
  };

  handleTabChange = tab => {
    runInAction(() => {
      this.activeTab = tab;
    });
    if (tab === TAB_IDLE) this.fetchIdleActivity();
  };

  // Arrow keys move between tabs, as the WAI-ARIA tabs pattern expects.
  handleTabKeyDown = event => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const index = TABS.findIndex(tab => tab.key === this.activeTab);
    const step = event.key === 'ArrowRight' ? 1 : -1;
    this.handleTabChange(TABS[(index + step + TABS.length) % TABS.length].key);
  };

  /**
   * Runs one write, reporting its outcome rather than throwing.
   *
   * The two writes are attempted independently, so one failing does not prevent the other from being
   * tried. They configure two independent features, and a user who changed both should not lose the
   * change that would have worked because of the one that would not.
   */
  submit = async save => {
    try {
      return { ok: true, value: await save() };
    } catch (error) {
      return { ok: false, error };
    }
  };

  /**
   * Brings the modal back in line with the authoritative state after the writes.
   *
   * Both write endpoints return a fresh SchedulingInfo, so the most recent successful response
   * describes both tags and no second read is needed. A caller that resolves with something else is not
   * treated as an error: the modal re-reads instead, which keeps this file working against a store
   * action that returns nothing. Either way the state is taken once, at the end, so that a write which
   * succeeded is reflected even when the other one failed.
   *
   * Only a section whose write succeeded has its controls reset. A section whose write failed keeps
   * what the user entered, so the value can be corrected and retried (Requirement 14.3).
   */
  applyAuthoritative = async (value, resets) => {
    if (isSchedulingInfo(value)) {
      runInAction(() => {
        this.applyInfo(value, resets);
      });
      return;
    }

    await this.fetchSchedulingInfo(resets);
  };

  /**
   * Saves whichever sections changed.
   *
   * Three properties matter here and are the reason this is not two calls in a row. Only the sections
   * that changed are written, because a write is an audited tag change and rewriting an identical value
   * would record an edit nobody made. Both are attempted, in a fixed order, so a failure in the first
   * does not hide the second. And the outcome is reported per section, so a write that failed is never
   * reported as saved and a write that succeeded is never lost.
   *
   * How the outcome is reported differs by direction, and deliberately. Successes go to the toast and
   * nowhere else, which is the pattern this area already uses (`displayError` here, `displaySuccess` on
   * the workspace card): once a write has gone through there is nothing left to do in the dialog, so it
   * closes, the same way `ModifyStorageSizeModal.handleConfirm` does. Failures stay inline and the
   * modal stays open, because the failed write is the one the user still has to correct and closing
   * would discard what they entered.
   */
  handleSave = async () => {
    const savingSchedule = this.canSubmitSchedule;
    const savingIdleStop = this.canSubmitIdleStop;
    if (!savingSchedule && !savingIdleStop) return;

    // Read before the first await, so that neither write can pick up a value the user changed while
    // the other was in flight.
    const scheduleValue = this.scheduleValue;
    const minutes = this.idleStopMinutes;
    const cpuThreshold = minutes === null ? undefined : this.cpuThreshold;

    runInAction(() => {
      this.submitting = true;
      this.error = '';
    });

    const schedule = savingSchedule ? await this.submit(() => this.props.onSaveSchedule(scheduleValue)) : null;
    const idleStop = savingIdleStop ? await this.submit(() => this.props.onSaveIdleStop(minutes, cpuThreshold)) : null;

    const scheduleSaved = !!schedule && schedule.ok;
    const idleStopSaved = !!idleStop && idleStop.ok;

    const saved = [];
    const failures = [];

    if (schedule) {
      if (scheduleSaved) saved.push(scheduleValue.length === 0 ? 'Schedule removed.' : 'Schedule saved.');
      else failures.push(`Schedule not saved: ${reasonFor(schedule.error)}`);
    }
    if (idleStop) {
      if (idleStopSaved) saved.push(minutes === null ? 'Idle stop turned off.' : 'Idle stop saved.');
      else failures.push(`Idle stop not saved: ${reasonFor(idleStop.error)}`);
    }

    if (saved.length > 0) displaySuccess(saved.join(' '), 'Scheduling updated');

    // The failures are not toasted. The inline message carries the backend's own wording, which names
    // the offending value and the rule it broke, and it sits next to the control that produced it; a
    // toast repeating it while the modal is still open would be the same sentence twice.
    if (failures.length === 0) {
      runInAction(() => {
        this.submitting = false;
      });

      // No authoritative re-read on the way out. The modal is closing, `componentDidUpdate` reads again
      // on the next open, and the store action has already fed the response to the ScEnvironment
      // through `setSchedulingState`, so the card behind the modal is right without a second request.
      this.props.onClose();
      return;
    }

    // Something failed, so the modal stays open. The state is still worth re-reading when a sibling
    // write went through, with the per-section reset flags, so that the section that saved refreshes
    // while the section that failed keeps what the user entered (Requirement 14.3).
    if (scheduleSaved || idleStopSaved) {
      // The later of the two successful responses is the more recent view of both tags.
      const authoritative = [schedule, idleStop].reduce(
        (acc, outcome) => (outcome && outcome.ok && isSchedulingInfo(outcome.value) ? outcome.value : acc),
        null,
      );

      await this.applyAuthoritative(authoritative, {
        resetScheduleControls: scheduleSaved,
        resetIdleControls: idleStopSaved,
      });
    }

    runInAction(() => {
      this.submitting = false;
      // Assigned after the re-read, so the write failure the user has to act on is what is shown even
      // when the re-read reported one of its own.
      this.error = failures.join(' ');
    });
  };

  // The close control is inert while a write is in flight (Requirement 14.2), so that a half-finished
  // write cannot be walked away from and then wondered about.
  handleClose = () => {
    if (!this.submitting) {
      this.props.onClose();
    }
  };

  renderLoading() {
    return (
      <div className="wsch-modal-loading" data-testid="scheduling-loading">
        <Icon name="spinner" loading size="large" />
        <span>Loading the scheduling settings...</span>
      </div>
    );
  }

  renderError() {
    if (!this.error) return null;

    return (
      <div className="wsch-modal-error" data-testid="scheduling-error">
        <Icon name="exclamation triangle" aria-hidden="true" />
        <div>{this.error}</div>
      </div>
    );
  }

  renderDriftNotice() {
    if (!this.driftNotice) return null;

    return (
      <div className="wsch-modal-notice" data-testid="scheduling-drift-notice" aria-live="polite">
        <Icon name="info circle" className="wsch-modal-notice-icon" aria-hidden="true" />
        <div className="wsch-modal-notice-text">
          <strong>These settings were changed outside Service Workbench.</strong> The values below were read from the
          workspace just now, and are what is actually in force.
        </div>
      </div>
    );
  }

  renderScheduleSection() {
    const disabled = this.busy;

    return (
      <div className="wsch-modal-section" data-testid="scheduling-schedule-section">
        {/* The tab names the section, so there is no heading here, only the subtext. */}
        <div className="wsch-modal-section-subtitle" data-testid="schedule-subtitle">
          <p>{this.scheduleSubtitle}</p>
          <p className="wsch-modal-section-example" data-testid="schedule-example">
            {SCHEDULE_EXAMPLE}
          </p>
        </div>

        <WorkspaceScheduleModeToggle mode={this.mode} onChange={this.handleModeChange} disabled={disabled} />

        {this.mode === MODE_GROUPS && (
          <WorkspaceScheduleGroupList
            groups={this.groups}
            selectedGroups={this.selectedGroups}
            onChange={this.handleGroupsChange}
            available={this.groupsAvailable}
            disabled={disabled}
          />
        )}

        {this.mode === MODE_CUSTOM && (
          <WorkspaceScheduleDayGrid
            days={this.days}
            timeOptions={this.timeOptions}
            onDayChange={this.handleDayChange}
            disabled={disabled}
          />
        )}

        <WorkspaceScheduleSummary lines={this.summaryLines} timezone={this.timezone} />
      </div>
    );
  }

  /**
   * The schedule tab's subtext: what a timetable does, what happens when the user overrides it, and
   * how soon a change takes effect. The timezone is left to the Schedule Summary's badge, which
   * states it once. The worked example under it is SCHEDULE_EXAMPLE.
   */
  get scheduleSubtitle() {
    const cadence = Number.isFinite(this.intervalMinutes)
      ? `Changes take effect within about ${this.intervalMinutes} minutes.`
      : "Changes take effect on the scheduler's next run.";
    return (
      'Start and stop your workspace automatically at set times. If you start or stop it yourself, it ' +
      `stays that way until the next scheduled start or stop. ${cadence}`
    );
  }

  /**
   * The idle tab's subtext: the benefit first, then what counts as idle, then the caveat that reading
   * or typing barely registers as CPU, whose cost of being missed is a user's unsaved work.
   */
  get idleSubtitle() {
    return IDLE_SUBTITLE;
  }

  renderIdleStopSection() {
    const idleStop = _.get(this.info, 'idleStop') || {};

    return (
      <div className="wsch-modal-section" data-testid="scheduling-idle-section">
        <div className="wsch-modal-section-subtitle" data-testid="idle-subtitle">
          {this.idleSubtitle}
        </div>

        <WorkspaceIdleStopSection
          minutes={this.idleStopMinutes}
          options={this.idleOptions}
          canUserSet={this.canUserSetIdleStop}
          lockedMinutes={normaliseMinutes(idleStop.lockedMinutes)}
          warnedAt={idleStop.warnedAt}
          projectedStopAt={idleStop.projectedStopAt}
          timezone={this.timezone}
          onChange={this.handleIdleStopChange}
          cpuThreshold={this.cpuThreshold}
          cpuThresholdOptions={this.cpuThresholdOptions}
          recommendedCpuThreshold={this.recommendedCpuThreshold}
          onCpuThresholdChange={this.handleCpuThresholdChange}
          disabled={this.busy}
        />

        {this.renderThresholdNotYetApplied()}

        {/* Only while its tab is showing: Chart.js sizes itself on mount, and a chart mounted inside a
            hidden panel would render at zero size. It has no state of its own to keep. */}
        {this.props.getIdleActivity && this.activeTab === TAB_IDLE && (
          <WorkspaceIdleActivityChart
            activity={this.activity}
            loading={this.activityLoading}
            error={this.activityError}
            minutes={this.idleStopMinutes}
            cpuThreshold={this.cpuThreshold}
            gpuThreshold={this.gpuThreshold}
            timezone={this.timezone}
          />
        )}
      </div>
    );
  }

  renderThresholdNotYetApplied() {
    if (!this.thresholdNotYetApplied || !this.canUserSetIdleStop) return null;

    const applied = normaliseMinutes(_.get(this.info, 'idleStop.effectiveCpuThreshold'));
    if (applied === this.cpuThreshold) return null;

    return (
      <div className="wsch-modal-note" data-testid="idle-threshold-not-applied">
        This workspace is currently judged against {applied === null ? 'the default threshold' : `${applied}%`}. Save to
        apply {this.cpuThreshold}%.
      </div>
    );
  }

  renderTabs() {
    const unsaved = { [TAB_SCHEDULE]: this.canSubmitSchedule, [TAB_IDLE]: this.canSubmitIdleStop };

    return (
      <div className="wsch-modal-tabs" role="tablist" aria-label={`${MODAL_TITLE} settings`}>
        {TABS.map(tab => {
          const selected = this.activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`wsch-modal-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`wsch-modal-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              className={`wsch-modal-tab${selected ? ' selected' : ''}`}
              onClick={() => this.handleTabChange(tab.key)}
              onKeyDown={this.handleTabKeyDown}
              data-testid={`scheduling-tab-${tab.key}`}
            >
              <Icon name={tab.icon} aria-hidden="true" />
              <span>{tab.label}</span>
              {unsaved[tab.key] && (
                <span className="wsch-modal-tab-unsaved" data-testid={`scheduling-tab-${tab.key}-unsaved`}>
                  <span className="wsch-modal-visually-hidden">(unsaved changes)</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  renderBody() {
    if (this.loading) return this.renderLoading();

    return (
      <>
        {this.renderError()}
        {this.renderDriftNotice()}
        {this.info === null ? null : (
          <>
            {this.renderTabs()}
            {/* Both panels stay mounted and the inactive one is hidden, so switching tabs keeps
                whatever the user entered and Save always sees both sections. */}
            {TABS.map(tab => (
              <div
                key={tab.key}
                role="tabpanel"
                id={`wsch-modal-panel-${tab.key}`}
                aria-labelledby={`wsch-modal-tab-${tab.key}`}
                className="wsch-modal-tab-panel"
                hidden={this.activeTab !== tab.key}
                data-testid={`scheduling-panel-${tab.key}`}
              >
                {tab.key === TAB_SCHEDULE ? this.renderScheduleSection() : this.renderIdleStopSection()}
              </div>
            ))}
          </>
        )}
      </>
    );
  }

  renderFooter() {
    return (
      <div className="wsch-modal-footer">
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          onClick={this.handleClose}
          disabled={this.submitting}
          data-testid="scheduling-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-restart"
          onClick={this.handleSave}
          disabled={!this.canSave}
          data-testid="scheduling-save"
        >
          {/* No inline style: .workspace-action-btn i.icon already carries the alignment reset. */}
          <Icon name="check" />
          Save
        </button>
      </div>
    );
  }

  render() {
    if (!this.props.open) return null;

    return ReactDOM.createPortal(
      <>
        <div className="wsch-modal-backdrop" onClick={this.submitting ? undefined : this.handleClose} />
        <div className="wsch-modal">
          <div className="wsch-modal-header">
            <div className="wsch-modal-header-title">
              <Icon name="clock outline" />
              {MODAL_TITLE}
            </div>
            {!this.submitting && (
              <button type="button" className="wsch-modal-close" onClick={this.handleClose} aria-label="Close">
                <Icon name="close" />
              </button>
            )}
          </div>
          <div className="wsch-modal-body">{this.renderBody()}</div>
          {this.renderFooter()}
        </div>
      </>,
      document.body,
    );
  }
}

decorate(WorkspaceSchedulingModal, {
  loading: observable,
  submitting: observable,
  error: observable,
  // The response and the two structured values are held by reference: they are replaced wholesale on
  // every change, and keeping them plain means the children receive plain objects and arrays.
  info: observable.ref,
  mode: observable,
  selectedGroups: observable.ref,
  days: observable.ref,
  idleStopMinutes: observable,
  driftNotice: observable,
  cpuThreshold: observable,
  activeTab: observable,
  activity: observable.ref,
  activityLoading: observable,
  activityError: observable,
  activityRequested: observable,
  groups: computed,
  groupNames: computed,
  groupsAvailable: computed,
  timezone: computed,
  intervalMinutes: computed,
  timeOptions: computed,
  idleOptions: computed,
  scheduleModel: computed,
  scheduleValue: computed,
  savedScheduleValue: computed,
  savedIdleStopMinutes: computed,
  savedCpuThreshold: computed,
  recommendedCpuThreshold: computed,
  cpuThresholdOptions: computed,
  gpuThreshold: computed,
  thresholdNotYetApplied: computed,
  scheduleSubtitle: computed,
  idleSubtitle: computed,
  canUserSetIdleStop: computed,
  busy: computed,
  canSubmitSchedule: computed,
  canSubmitIdleStop: computed,
  canSave: computed,
  summaryLines: computed,
  applyInfo: action,
  fetchSchedulingInfo: action,
  submit: action,
  applyAuthoritative: action,
  handleModeChange: action,
  handleGroupsChange: action,
  handleDayChange: action,
  handleIdleStopChange: action,
  handleCpuThresholdChange: action,
  handleTabChange: action,
  fetchIdleActivity: action,
  handleSave: action,
  handleClose: action,
});

export default observer(WorkspaceSchedulingModal);
export { MODAL_TITLE, SCHEDULE_EXAMPLE, IDLE_SUBTITLE };
